import { useAuth0 } from "@auth0/auth0-react";
import {
  BusinessOutlined,
  ChangeCircleOutlined,
  HandymanOutlined,
  Person2Outlined,
  PowerSettingsNewOutlined,
  ReceiptLongOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import { Divider, Menu, MenuItem, Typography } from "@mui/material";

import { useSnackbar } from "@components";
import { useCreateBillingSessionMutation } from "@hooks";
import { Organization, User } from "@models";
import { fillRouteWithSlugs } from "@utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "src/config";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { clearCachedTiptapCollaborationTokens } from "src/services/tiptap-collaboration-token";
import { SwitchOrgMenu } from "./SwitchOrgMenu";
import { UserCard } from "./UserCard";

type Props = {
  anchorUserMenu: HTMLElement | null;
  isOpen: boolean;
  close: () => void;
  user: User;
  org: Organization;
  anchorOrgMenu: Element | null;
  isOrgMenuOpen: boolean;
  closeOrgMenu: () => void;
  clickOrgMenuIcon: (e: React.MouseEvent<Element, MouseEvent>) => void;
};

export const UserMenu = ({
  anchorUserMenu,
  isOpen,
  close,
  org,
  user,
  anchorOrgMenu,
  isOrgMenuOpen,
  closeOrgMenu,
  clickOrgMenuIcon,
}: Props) => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const { orgId = "" } = useParams();
  const { showSnackbar } = useSnackbar();
  const { mutateAsync: createBillingSession } =
    useCreateBillingSessionMutation();
  const isUserSettingsEnabled = useFeatureFlagEnabled("user-settings-menu");
  if (!user || !orgId) return null;
  const canManageConfiguration = !!user.permissions?.includes(
    "write:configuration"
  );

  const goToUserSettings = () => {
    if (!orgId) return;
    navigate(
      fillRouteWithSlugs(ROUTES.USER_SETTINGS, { [ROUTE_SLUGS.ORG_ID]: orgId })
    );
    close();
  };

  const goToOrgSettings = () => {
    if (!orgId) return;
    navigate(
      fillRouteWithSlugs(ROUTES.MANAGE_ORGANIZATION, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
      })
    );
    close();
  };

  const clickableRowSx = {
    cursor: "pointer",
    "& .settings-cog": { opacity: 0, transition: "opacity 120ms ease-in" },
    "&:hover": {
      bgcolor: "rgba(0, 0, 0, 0.04)",
      "& .settings-cog": { opacity: 1 },
    },
  } as const;

  const handleGoToBilling = async () => {
    const billingUrl = await createBillingSession(orgId);
    if (billingUrl) {
      window.open(billingUrl, "_blank");
    } else {
      showSnackbar(
        "Failed to create billing session. Please try again or contact support."
      );
    }
  };

  return (
    <Menu
      id="user-menu"
      anchorEl={anchorUserMenu}
      open={isOpen}
      onClose={close}
      onClick={close}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
      disablePortal
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {isUserSettingsEnabled ? (
        <MenuItem
          onMouseEnter={closeOrgMenu}
          onClick={goToUserSettings}
          sx={clickableRowSx}
          className="gap-3"
        >
          <Person2Outlined />
          <Typography variant="body1" className="text-wrap flex-1">
            {user.firstName} {user.lastName}
          </Typography>
          <SettingsOutlined
            fontSize="small"
            className="settings-cog"
            sx={{ color: "rgba(0, 0, 0, 0.54)" }}
          />
        </MenuItem>
      ) : (
        <div className="mx-[6px] my-[4px] flex items-center gap-3 rounded px-[10px] py-[6px]">
          <Person2Outlined />
          <Typography variant="body1" className="text-wrap">
            {user.firstName} {user.lastName}
          </Typography>
        </div>
      )}
      {orgId ? (
        <MenuItem
          onMouseEnter={closeOrgMenu}
          onClick={goToOrgSettings}
          sx={clickableRowSx}
          className="gap-3"
        >
          <BusinessOutlined />
          <Typography variant="body1" className="text-wrap flex-1">
            {org?.name}
          </Typography>
          <SettingsOutlined
            fontSize="small"
            className="settings-cog"
            sx={{ color: "rgba(0, 0, 0, 0.54)" }}
          />
        </MenuItem>
      ) : (
        <div className="mx-[6px] my-[4px] flex items-center gap-3 rounded px-[10px] py-[6px]">
          <BusinessOutlined />
          <Typography variant="body1" className="text-wrap">
            {org?.name}
          </Typography>
        </div>
      )}

      <Divider className="my-2" />

      <UserCard
        onClick={() => {
          close();
          handleGoToBilling();
        }}
        text={"Billing"}
        icon={<ReceiptLongOutlined />}
        onMouseEnter={closeOrgMenu}
      />
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          clickOrgMenuIcon(e);
        }}
        sx={{
          zIndex: 20,
          "&:hover": {
            bgcolor: "rgba(0, 0, 0, 0.04)",
            cursor: "pointer",
          },
        }}
        className="gap-3"
      >
        <ChangeCircleOutlined />
        <Typography variant="body1" className="text-wrap">
          Switch Workspace
        </Typography>
      </MenuItem>
      <SwitchOrgMenu
        anchorOrgMenu={anchorOrgMenu}
        isOrgMenuOpen={isOrgMenuOpen}
        closeUserMenu={close}
        closeOrgMenu={closeOrgMenu}
        user={user}
      />
      <UserCard
        onClick={() => {
          clearCachedTiptapCollaborationTokens();
          logout({ returnTo: window.location.origin });
        }}
        key="logout"
        text="Logout"
        onMouseEnter={closeOrgMenu}
        icon={<PowerSettingsNewOutlined />}
      />
      {orgId && canManageConfiguration && (
        <UserCard
          to={fillRouteWithSlugs(ROUTES.ADMIN_TASK_DEFINITIONS, {
            [ROUTE_SLUGS.ORG_ID]: orgId,
          })}
          text={"Admin"}
          icon={<HandymanOutlined />}
          onClick={close}
          onMouseEnter={closeOrgMenu}
        />
      )}
    </Menu>
  );
};
